import {
  AddressInputModel,
  BusinessDetails,
  CreateBusinessInputModel,
  MCCCodeModel,
  MerchantAccount,
  MerchantAccountDetails,
  MerchantApplicationInputModel,
  PrimaryContactDetails,
} from "../../shared/models";
import {
  IAPICommonResponse,
  IAPIResponse,
  PaginationModel,
  ToastService,
} from "@mindmemobile/mm-shared-global";
import { action, makeAutoObservable, observable } from "mobx";
import { firstValueFrom } from "rxjs";
import { CountryStore } from "../../shared/store";
import { AuthService, MerchantAccountService } from "../../shared/services";
import { ApplicationStatus } from "../../shared/enums";
import { IAPIUpdateApplicationStateModel } from "../../shared/interfaces";

export default class MerchantAccountStore {
  @observable applicationData: MerchantApplicationInputModel;
  @observable businessData: CreateBusinessInputModel;
  @observable isNewBusiness: boolean;
  @observable businesses: BusinessDetails[];
  @observable completeApplicationLink: string;
  @observable merchantAccounts: MerchantAccount[];
  @observable mccCodes: MCCCodeModel[];
  @observable accountName: string;
  public pagination: PaginationModel = new PaginationModel({
    pageNumber: 1,
    pageSize: 999999,
  });
  @observable cardLink: string;
  countryStore: CountryStore;
  @observable isSaveInProgress: boolean;
  @observable isBusinessSubmission: boolean;
  public isMerchantAccountsLoadingInProgress: boolean = true;

  constructor() {
    makeAutoObservable(this);
    this.applicationData = new MerchantApplicationInputModel();
    this.businessData = new CreateBusinessInputModel();
    this.isNewBusiness = false;
    this.businesses = [];
    this.completeApplicationLink = "";
    this.merchantAccounts = [];
    this.mccCodes = [];
    this.cardLink = "";
    this.accountName = "";
    this.countryStore = new CountryStore();
    this.isSaveInProgress = false;
    this.isBusinessSubmission = false;
    this.isMerchantAccountsLoadingInProgress = false;

    this.countryStore.onCountryDataLoaded.subscribe((countryData) => {
      if (countryData) {
        const usCountry = countryData.find((c) => c.abbreviationName === "US");
        if (usCountry) {
          this.applicationData = new MerchantApplicationInputModel({
            ...this.applicationData,
            address: new AddressInputModel({
              ...this.applicationData.address,
              countryDetails: usCountry,
            }),
            accountDetails: new MerchantAccountDetails({
              ...this.applicationData.accountDetails,
              countryDetails: usCountry,
            }),
          });

          this.businessData = new CreateBusinessInputModel({
            ...this.businessData,
            primaryContactDetails: new PrimaryContactDetails({
              ...this.businessData.primaryContactDetails,
              countryDetails: usCountry,
            }),
          });
        }
      }
    });
  }

  @action
  toggleBusinessState(): void {
    this.isNewBusiness = !this.isNewBusiness;
  }

  @action
  updateApplicationData(data: MerchantApplicationInputModel): void {
    this.applicationData = new MerchantApplicationInputModel(data);
  }

  @action
  updateBusinessData(data: CreateBusinessInputModel): void {
    this.businessData = new CreateBusinessInputModel(data);
  }

  @action
  updateFormSubmissionState(state: boolean) {
    this.isSaveInProgress = state;
  }

  @action
  updateBusinessSubmissionState(state: boolean) {
    this.isBusinessSubmission = state;
  }

  @action
  validateDetails() {
    const isValid = this.applicationData.validate();
    this.applicationData = new MerchantApplicationInputModel({
      ...this.applicationData,
    });
    return isValid;
  }

  @action
  async submitNewBusiness(): Promise<void> {
    const isValid = this.businessData.validate();
    if (!isValid) {
      this.businessData = new CreateBusinessInputModel({
        ...this.businessData,
      });
      return;
    }
    this.updateBusinessSubmissionState(true);
    const apiModel = this.businessData.createBusinessAPIModel();
    const isValidName = await this.checkBusinessName(apiModel.name);

    if (isValidName) {
      const response =
        await MerchantAccountService.createBusiness(apiModel).toPromise();

      if (response?.result) {
        if (!AuthService.hasAccount && response?.result.accountId) {
          AuthService.setAuthData(response?.result.accountId);
        }
        await this.fetchBusinesses(apiModel.name);
        this.toggleBusinessState();
        this.setBusinessNameError(false);
        this.updateBusinessSubmissionState(false);
      }
    } else {
      this.updateBusinessSubmissionState(false);
    }
  }

  @action
  async checkBusinessName(businessName: string): Promise<boolean> {
    if (
      !AuthService.hasAccount ||
      AuthService.mindmeAccountDetails?.subAccountId
    ) {
      return true;
    }
    return await MerchantAccountService.getIsValidBusiness(
      AuthService.authData.accountId,
      businessName
    )
      .toPromise()
      .then((response) => {
        if (response && !response.result) {
          return !response.result;
        } else {
          this.setBusinessNameError(true);
          return false;
        }
      });
  }

  @action
  setBusinessNameError(error: boolean): void {
    if (error) {
      this.updateBusinessData({
        ...this.businessData,
        businessNameError: "Business name already exists",
      } as CreateBusinessInputModel);
    } else {
      this.updateBusinessData({
        ...this.businessData,
        businessNameError: "",
      } as CreateBusinessInputModel);
    }
  }

  @action
  async fetchBusinesses(businessName?: string): Promise<BusinessDetails[]> {
    if (!AuthService.hasAccount) {
      return [];
    }
    const response = await MerchantAccountService.getBusinesses(
      AuthService.authData.accountId
    ).toPromise();
    if (response?.result) {
      const businessData: BusinessDetails[] = response.result;
      this.setBusinesses(businessData);
      if (businessName) {
        const selectedBusiness = businessData.find(
          (item) => item.name === businessName
        );
        this.updateSelectedBusiness(selectedBusiness);
      }
    }
    return response?.result;
  }

  @action
  async fetchMCCCodes(): Promise<MCCCodeModel[]> {
    if (!AuthService.hasAccount) {
      return [];
    }
    const response = await MerchantAccountService.getFiservMCCS().toPromise();
    if (response?.result) {
      this.mccCodes =
        response.result?.map((m) => MCCCodeModel.deserializeAPIModel(m)) || [];
    }
    return [];
  }

  @action
  updateSelectedBusiness(selectedBusiness: BusinessDetails | undefined): void {
    const updatedApplicationData = {
      ...this.applicationData,
      businessName: selectedBusiness?.name || "",
      businessId: selectedBusiness?.id || "",
    };
    this.updateApplicationData(
      new MerchantApplicationInputModel(updatedApplicationData)
    );
  }

  @action
  setBusinesses(businesses: BusinessDetails[]): void {
    this.businesses = businesses;
  }

  @action
  async submitApplication(): Promise<string> {
    this.isSaveInProgress = true;
    const isValid = this.validateDetails();
    if (isValid) {
      try {
        const apiModel = this.applicationData.createAPIModel();
        const response =
          await MerchantAccountService.createMerchantAccount(
            apiModel
          ).toPromise();
        if (response?.result.id) {
          this.fetchMerchantAccounts();
          this.completeApplicationLink =
            response?.result.completeApplicationLink;
          return response?.result.id;
        }
        return "";
      } catch (error) {
        console.error("Error submitting application", error);
        return "";
      }
    } else {
      return "";
    }
  }

  setMerchantAccountsData(merchantAccounts: MerchantAccount[]) {
    this.merchantAccounts = merchantAccounts;
  }

  @action
  async fetchMerchantAccounts() {
    if (!AuthService.hasAccount) {
      this.isMerchantAccountsLoadingInProgress = false;
      return;
    }

    this.isMerchantAccountsLoadingInProgress = true;

    try {
      const response = await MerchantAccountService.getMerchantAccounts(
        AuthService.authData.accountId,
        this.pagination.pageNumber,
        this.pagination.pageSize,
        ""
      ).toPromise();

      this.isMerchantAccountsLoadingInProgress = false;
      const mappedData =
        response?.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
        [];
      this.setMerchantAccountsData(mappedData);
    } catch (error) {
      this.isMerchantAccountsLoadingInProgress = false;
      console.error("Error fetching businesses", error);
    }
  }

  @action
  resetApplicationData(): void {
    this.applicationData = new MerchantApplicationInputModel({
      accountDetails: new MerchantAccountDetails({
        countryDetails: this.countryStore.getUSCountry(),
      }),
      address: new AddressInputModel({
        countryDetails: this.countryStore.getUSCountry(),
      }),
    });
  }

  @action
  resetBusinessData(): void {
    this.businessData = new CreateBusinessInputModel({
      primaryContactDetails: new PrimaryContactDetails({
        countryDetails: this.countryStore.getUSCountry(),
      }),
    });
  }

  @action
  fetchCompleteApplicationLink(id: string) {
    MerchantAccountService.getCompleteApplicationLink(
      AuthService.authData.accountId,
      id
    ).subscribe(
      (response) => {
        this.cardLink = response.result;
        if (this.cardLink) {
          window.open(this.cardLink, "_blank");
        }
      },
      (error) => {
        console.error("Error fetching businesses", error);
      }
    );
    return this.businesses;
  }

  @action
  async updateApplicationState(
    id: string,
    status: keyof typeof ApplicationStatus
  ): Promise<IAPIResponse<IAPICommonResponse>> {
    const requestData: IAPIUpdateApplicationStateModel = {
      accountId: AuthService.authData.accountId,
      applicationId: id,
      status: status,
    };

    const response = await firstValueFrom(
      MerchantAccountService.updateApplicationStatus(requestData)
    );
    // if (response.result) {
    //   ToastService.showSuccess("Application status updated successfully");
    // } else {
    //   ToastService.showError("Error updating application status");
    // }
    // this.fetchMerchantAccounts();
    return response;
  }
  @action
  hasNoActiveAccounts(): boolean {
    return (
      this.merchantAccounts.length > 0 &&
      !this.merchantAccounts.some(
        (account) => account.applicationStatus === ApplicationStatus.Active
      ) &&
      !this.isMerchantAccountsLoadingInProgress
    );
  }

  @action
  hasSinglePendingMerchantAccount(): boolean {
    return (
      this.merchantAccounts.length === 1 &&
      this.merchantAccounts[0].applicationStatus ===
        ApplicationStatus.Pending &&
      !this.isMerchantAccountsLoadingInProgress
    );
  }

  @action
  hasMultiplePendingMerchantAccounts(): boolean {
    return (
      this.merchantAccounts.filter(
        (m) => m.applicationStatus === ApplicationStatus.Pending
      ).length > 1 && !this.isMerchantAccountsLoadingInProgress
    );
  }

  hasNotificationBar(): boolean {
    return (
      !AuthService.isMerchantAccountsRoute &&
      (this.hasNoActiveAccounts() || this.hasSinglePendingMerchantAccount())
    );
  }

  @action
  getPendingMerchantAccountId(): string {
    return (
      this.merchantAccounts.find(
        (m) => m.applicationStatus === ApplicationStatus.Pending
      )?.id || ""
    );
  }
}
